import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import FileUpload from '../../components/UI/FileUpload';
import NotFound from '../../components/UI/NotFound';
import { imageType, MAX_FILE_SIZE_IMAGE, invalidImageMessage, imageSizeExceededMessage } from '../../components/imageType';
import { FaImage, FaUpload, FaRegEye, FaLink, FaEdit, FaTrashAlt, FaTimes } from 'react-icons/fa';
import { HiOutlineRefresh } from 'react-icons/hi';
import { formatView } from '../../components/formatView';
import { useNavigate } from 'react-router-dom';

const Banner = () => {
    const navigate = useNavigate();
    const [loader, setLoader] = useState(false);
    const [banners, setBanners] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [bannerType, setBannerType] = useState('url');
    const [bannerCategory, setBannerCategory] = useState('');
    const [bannerList, setBannerList] = useState('');
    const [bannerUrl, setBannerUrl] = useState('');
    const [error, setError] = useState(null);
    const [editBanner, setEditBanner] = useState(null);
    const [bannerStatus, setBannerStatus] = useState(true);
    const [previewImage, setPreviewImage] = useState(null);
    const [fileProcessing, setFileProcessing] = useState({});
    const [fileStatus, setFileStatus] = useState({});
    const [selectedBanners, setSelectedBanners] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        fetchBanners();
    }, []);

    useEffect(() => {
        if (bannerType === 'category') {
            fetchCategories();
        }
    }, [bannerType]);

    const fetchCategories = async () => {
        try {
            const resp = await Calling.getCategory();
            if (resp?.status) {
                setCategories(resp?.data || []);
            } else {
                toast.error(resp?.message || "Failed to fetch categories");
            }
        } catch (error) {
            console.error("Fetch categories error:", error);
            toast.error("Failed to fetch categories");
        }
    };

    const fetchBanners = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getBanner();
            if (resp?.status) {
                setBanners(resp?.data);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Fetch error:", error.response?.data || error.message);
            toast.error("Failed to fetch banners");
        } finally {
            setLoader(false);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const fieldName = event.target.name || 'image';

        setError(null);
        setSelectedFile(null);
        setFileProcessing({ ...fileProcessing, [fieldName]: true });
        setFileStatus({ ...fileStatus, [fieldName]: null });
        setPreviewImage(null);

        if (!file) {
            setError('No file selected.');
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        if (!imageType.includes(file.type)) {
            setError(invalidImageMessage);
            toast.error(invalidImageMessage);
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        if (file.size > MAX_FILE_SIZE_IMAGE) {
            setError(imageSizeExceededMessage);
            toast.error(imageSizeExceededMessage);
            setFileProcessing({ ...fileProcessing, [fieldName]: false });
            setFileStatus({ ...fileStatus, [fieldName]: 'error' });
            return;
        }

        setSelectedFile(file);
        setFileProcessing({ ...fileProcessing, [fieldName]: false });
        setFileStatus({ ...fileStatus, [fieldName]: 'success' });

        // Create preview URL for the selected file
        const previewUrl = URL.createObjectURL(file);
        setPreviewImage(previewUrl);
        setShowPreview(true);
    };

    const resetForm = () => {
        setSelectedFile(null);
        setBannerType('url');
        setBannerCategory('');
        setBannerList('');
        setBannerUrl('');
        setError(null);
        setFileStatus({});
        setEditBanner(null);
        setPreviewImage(null);
        setShowPreview(false);
        setBannerStatus(true);
    };

    const handleSubmit = async (e, bannerId = null) => {
        e.preventDefault();

        if (!bannerId && !selectedFile) {
            setError('Banner image is required.');
            return;
        }

        // Check for required fields based on banner type
        if (bannerType === 'category' && !bannerCategory) {
            setError('Category is required for category type banner.');
            return;
        }

        if (bannerType === 'list' && !bannerList) {
            setError('List is required for list type banner.');
            return;
        }

        if (bannerType === 'url' && !bannerUrl) {
            setError('URL is required for URL type banner.');
            return;
        }

        // For edit mode, at least one field should be provided
        if (bannerId && !selectedFile && bannerType === editBanner.b_type) {
            if ((bannerType === 'category' && !bannerCategory) ||
                (bannerType === 'list' && !bannerList) ||
                (bannerType === 'url' && !bannerUrl)) {
                setError('Please update at least one field.');
                return;
            }
        }

        setLoader(true);
        try {
            // Create form data with all necessary fields
            const formData = new FormData();
            if (selectedFile) {
                formData.append('image', selectedFile);
            }

            formData.append('b_type', bannerType);

            if (bannerType === 'category') {
                formData.append('b_category', bannerCategory);
            } else if (bannerType === 'list') {
                formData.append('b_list', bannerList);
            } else if (bannerType === 'url') {
                formData.append('b_url', bannerUrl);
            }

            // Include status for edit mode
            if (bannerId) {
                formData.append('b_status', bannerStatus);
            }

            let resp;
            if (bannerId) {
                resp = await Calling.updateBanner(bannerId, formData);
            } else {
                resp = await Calling.addBanner(formData);
            }

            if (resp?.status) {
                toast.success(resp?.message);
                resetForm();
                await fetchBanners();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Submission error:", error.response?.data || error.message);
            toast.error("Something went wrong.");
        } finally {
            setLoader(false);
        }
    };

    const handleEdit = (banner) => {
        setEditBanner(banner);
        setBannerType(banner.b_type);

        if (banner.b_type === 'category') {
            setBannerCategory(banner.b_category);
        } else if (banner.b_type === 'list') {
            setBannerList(banner.b_list);
        } else if (banner.b_type === 'url') {
            setBannerUrl(banner.b_url);
        }

        setBannerStatus(banner.b_status);
        setPreviewImage(banner.b_image);
        setShowPreview(true);
        setFileStatus({});
    };

    const handleDelete = async () => {
        if (selectedBanners.length === 0) {
            toast.error('Please select at least one banner to delete.');
            return;
        }

        const content = "You are about to delete selected items. To confirm, please solve this math problem.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            const resp = await Calling.deleteBanner({ id: selectedBanners });
            if (resp?.status) {
                toast.success(resp?.message);
                setBanners(banners.filter((banner) => !selectedBanners.includes(banner._id)));
                setSelectedBanners([]);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Delete error:", error.response?.data || error.message);
            toast.error("Failed to delete banners.");
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateStatus = async (id) => {
        const content = "You are about to update this banner's status. To confirm, please solve this math problem.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            // Get the current banner
            const currentBanner = banners.find(banner => banner._id === id);

            // Create formData with updated status (toggled)
            const formData = new FormData();
            formData.append('b_status', !currentBanner.b_status);
            formData.append('b_type', currentBanner.b_type);

            // Include the relevant field based on banner type
            if (currentBanner.b_type === 'category') {
                formData.append('b_category', currentBanner.b_category);
            } else if (currentBanner.b_type === 'list') {
                formData.append('b_list', currentBanner.b_list);
            } else if (currentBanner.b_type === 'url') {
                formData.append('b_url', currentBanner.b_url);
            }

            const resp = await Calling.updateBanner(id, formData);
            if (resp?.status) {
                toast.success(resp?.message);
                fetchBanners();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Status update error:", error.response?.data || error.message);
            toast.error("Failed to update status.");
        } finally {
            setLoader(false);
        }
    };

    const handleSelectBanner = (id) => {
        if (selectedBanners.includes(id)) {
            setSelectedBanners(selectedBanners.filter(bannerId => bannerId !== id));
        } else {
            setSelectedBanners([...selectedBanners, id]);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedBanners(banners.map(banner => banner._id));
        } else {
            setSelectedBanners([]);
        }
    };

    const closePreview = () => {
        setShowPreview(false);
    };

    const getBannerTypeDisplay = (type, data) => {
        switch (type) {
            case 'category':
                return `Category: ${data.b_category}`;
            case 'list':
                return `List: ${data.b_list}`;
            case 'url':
                return `URL: ${data.b_url}`;
            default:
                return 'Unknown Type';
        }
    };

    const handleClick = async (id) => {
        navigate(`/views/dashboard/${id}`);
    }

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className="w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
                    <div className="mx-auto">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Banner Management</h1>
                                <p className="mt-1 text-sm text-gray-500">Manage banners for your application</p>
                            </div>
                            <button
                                onClick={fetchBanners}
                                className="flex items-center gap-2 px-4 py-2 bg-main-blue hover:bg-main-blue/80 rounded-lg text-white transition-colors duration-200 shadow-sm"
                            >
                                <HiOutlineRefresh className="text-lg" />
                                <span>Refresh</span>
                            </button>
                        </div>

                        {/* Add Banner Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100 mb-6">
                            <div className="p-5 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                        <FaUpload className="text-main-blue text-xl" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">{editBanner ? 'Edit Banner' : 'Add Banner'}</h2>
                                        <p className="text-xs text-gray-500 mt-0.5">{editBanner ? 'Update existing banner' : 'Add new banner to your application'}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                <form onSubmit={(e) => handleSubmit(e, editBanner?._id)} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="md:col-span-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaImage className="text-main-blue text-sm" />
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Banner Image
                                                </label>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-2">Upload banner image</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <div className="relative">
                                                <FileUpload
                                                    name="image"
                                                    id="image"
                                                    onChange={handleFileChange}
                                                    accept={imageType.join(',')}
                                                    required={!editBanner}
                                                    multiple={false}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                                />
                                                {fileProcessing['image'] && (
                                                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                        <div className="loader"></div>
                                                    </div>
                                                )}
                                                {fileStatus['image'] === 'error' && (
                                                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-red-500">✘</div>
                                                )}
                                                {fileStatus['image'] === 'success' && (
                                                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xl text-green-500">✓</div>
                                                )}
                                            </div>
                                            <p className="text-xs mt-1 text-gray-400">Allow png, jpg, jpeg / Max size: {MAX_FILE_SIZE_IMAGE / (1024 * 1024)} MB</p>

                                            {/* Image Preview */}
                                            {showPreview && previewImage && (
                                                <div className="mt-3 relative">
                                                    <div className="border rounded-lg p-2 inline-block relative">
                                                        <button
                                                            type="button"
                                                            onClick={closePreview}
                                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                                        >
                                                            <FaTimes size={12} />
                                                        </button>
                                                        <img
                                                            src={previewImage}
                                                            alt="Preview"
                                                            className="h-24 w-auto object-contain rounded"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="md:col-span-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaEdit className="text-main-blue text-sm" />
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Banner Type
                                                </label>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-2">Select banner type</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <select
                                                name="bannerType"
                                                id="bannerType"
                                                value={bannerType}
                                                onChange={(e) => setBannerType(e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                            >
                                                <option value="url">URL</option>
                                                <option value="category">Category</option>
                                                <option value="list">List</option>
                                            </select>
                                        </div>
                                    </div>

                                    {bannerType === 'category' && (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="md:col-span-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <FaLink className="text-main-blue text-sm" />
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Category
                                                    </label>
                                                </div>
                                                <p className="text-xs text-gray-500 mb-2">Select a category</p>
                                            </div>
                                            <div className="md:col-span-2">
                                                <select
                                                    name="bannerCategory"
                                                    id="bannerCategory"
                                                    value={bannerCategory}
                                                    onChange={(e) => setBannerCategory(e.target.value)}
                                                    required={bannerType === 'category'}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                                >
                                                    <option value="">Select Category</option>
                                                    {categories.map((category) => (
                                                        <option key={category._id} value={category._id}>
                                                            {category.category_name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    {bannerType === 'list' && (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="md:col-span-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <FaLink className="text-main-blue text-sm" />
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        List
                                                    </label>
                                                </div>
                                                <p className="text-xs text-gray-500 mb-2">Enter list ID</p>
                                            </div>
                                            <div className="md:col-span-2">
                                                <input
                                                    type="text"
                                                    name="bannerList"
                                                    id="bannerList"
                                                    value={bannerList}
                                                    onChange={(e) => setBannerList(e.target.value)}
                                                    required={bannerType === 'list'}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                                    placeholder="Enter list ID"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {bannerType === 'url' && (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="md:col-span-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <FaLink className="text-main-blue text-sm" />
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        URL
                                                    </label>
                                                </div>
                                                <p className="text-xs text-gray-500 mb-2">Enter URL for banner</p>
                                            </div>
                                            <div className="md:col-span-2">
                                                <input
                                                    type="url"
                                                    name="bannerUrl"
                                                    id="bannerUrl"
                                                    value={bannerUrl}
                                                    onChange={(e) => setBannerUrl(e.target.value)}
                                                    required={bannerType === 'url'}
                                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                                    placeholder="https://example.com"
                                                />
                                                <p className={`text-xs mt-2 ${error ? 'text-red-500' : 'text-gray-500'}`}>{error ? error : ''}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-end gap-3 pt-2">
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                                        >
                                            Reset
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 transition-colors duration-200 flex items-center gap-2"
                                        >
                                            <FaUpload className="text-sm" />
                                            {editBanner ? 'Update Banner' : 'Save Banner'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Banners List Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
                            <div className="p-5 border-b border-gray-100">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                            <FaRegEye className="text-main-blue text-xl" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-800">Banners</h2>
                                            <p className="text-xs text-gray-500 mt-0.5">Manage your existing banners</p>
                                        </div>
                                    </div>
                                    {selectedBanners.length > 0 && (
                                        <button
                                            onClick={handleDelete}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center gap-2"
                                        >
                                            <FaTrashAlt className="text-sm" />
                                            Delete Selected ({selectedBanners.length})
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                {banners.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 text-main-blue focus:ring-main-blue border-gray-300 rounded"
                                                            onChange={handleSelectAll}
                                                            checked={selectedBanners.length === banners.length && banners.length > 0}
                                                        />
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Image
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Type
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Details
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Click
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {banners.map((banner) => (
                                                    <tr key={banner._id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <input
                                                                type="checkbox"
                                                                className="h-4 w-4 text-main-blue focus:ring-main-blue border-gray-300 rounded"
                                                                onChange={() => handleSelectBanner(banner._id)}
                                                                checked={selectedBanners.includes(banner._id)}
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex-shrink-0 h-12 w-20">
                                                                <img
                                                                    className="h-12 w-20 object-cover rounded"
                                                                    src={banner.b_image}
                                                                    alt="Banner"
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-gray-900 capitalize">{banner.b_type}</div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="text-sm text-gray-500 max-w-xs truncate">
                                                                {banner.b_type === 'url' && (
                                                                    <a href={banner.b_url} target="_blank" rel="noopener noreferrer" className="text-main-blue hover:underline">
                                                                        {banner.b_url}
                                                                    </a>
                                                                )}
                                                                {banner.b_type === 'category' && (
                                                                    <span>Category ID: {banner.b_category}</span>
                                                                )}
                                                                {banner.b_type === 'list' && (
                                                                    <span>List ID: {banner.b_list}</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={banner.b_status}
                                                                    onChange={() => handleUpdateStatus(banner._id)}
                                                                    className="sr-only peer"
                                                                />
                                                                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                                            peer-checked:after:translate-x-full peer-checked:after:border-white 
                                            after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                            after:bg-white after:border-gray-300 after:border after:rounded-full 
                                            after:h-5 after:w-5 after:transition-all dark:border-gray-600 
                                            peer-checked:bg-main-blue"></div>
                                                            </label>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-main-blue cursor-pointer" onClick={() => handleClick(banner._id)}>{formatView(banner.b_click)}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                            <button
                                                                onClick={() => handleEdit(banner)}
                                                                className="flex items-center px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <NotFound />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Banner;